import {
  useMutation,
  useQueryClient,
  type QueryClient,
  type UseMutationOptions,
} from "@tanstack/react-query";

import type { SpecSummaryEditBrowserInput } from "@/api/contracts/spec-editor";
import { specSummaryEdit } from "@/api/functions/spec-editor";
import type { specificationSummary } from "@/api/functions/specifications";
import {
  editorMutationOptions,
  type EditorMutationResult,
  type EditorSnapshot,
} from "@/hooks/spec-editor-cache";
import {
  followCommunityMoveFrom,
  specificationSummaryByVersionQueryKey,
  specificationSummaryQueryKey,
  specificationsListQueryKeyPrefix,
} from "@/hooks/specifications";

// The Editor header pencil's hook (#799), in a module of its own rather than
// beside the list's rename in `@/hooks/specifications`: that module is read by
// every spec surface, and the Editor's verbs reach the cache updater, which
// reads it in turn — so the header hook sits with the Editor's, and borrows the
// rename's community follow-up (`followCommunityMoveFrom`) rather than
// restating it.

/**
 * The Editor header pencil's write (#799): the spec's name, its Community ID
 * and its notes, from the draft's header — both of its dialogs (#806) call
 * this. It runs through the Editor's harness (`editorMutationOptions`) like
 * every verb on the version, so a header save that overlaps a pending move
 * re-lays that move's guess over its result rather than writing the old order
 * back. Its result lands through the one updater (`applyEditorResult`): the
 * new summary lands in the header's keys in the same frame, and every lines
 * view stays as it is. Then, as the list's rename does, the spec list's
 * entries are marked stale **without refetching** (the name and Community ID
 * columns changed; the way back reads afresh), and a Community ID that moves
 * the spec to another community follows the list rename's rule (#751 D1) —
 * keyed by the community the cached summary named before the write, which
 * `onMutate` reads while the cache still holds it.
 *
 * Not optimistic, as no lifecycle write is; a failure rejects with the
 * server's error (no `ERRORS` key) and touches nothing. Whether the dialog
 * shows it is #806's (the source's dialog closed silently on a failure).
 */
export function editSpecificationSummaryMutationOptions(
  queryClient: QueryClient,
): UseMutationOptions<
  EditorMutationResult,
  Error,
  SpecSummaryEditBrowserInput,
  EditorSnapshot & { communityId: number | undefined }
> {
  const base = editorMutationOptions<SpecSummaryEditBrowserInput>(queryClient, {
    mutationFn: (data) => specSummaryEdit({ data }),
  });
  return {
    mutationFn: base.mutationFn,
    onMutate: async (input, context) => {
      const summary =
        queryClient.getQueryData<CachedSummary>(
          specificationSummaryQueryKey(input.specificationId),
        ) ??
        queryClient.getQueryData<CachedSummary>(
          specificationSummaryByVersionQueryKey(input.versionId),
        );
      const snapshot = await base.onMutate!(input, context);
      return { ...snapshot, communityId: summary?.community?.id };
    },
    onError: base.onError,
    onSuccess: async (result, input, snapshot, context) => {
      followCommunityMoveFrom(queryClient, snapshot.communityId, input.communityId);
      await base.onSuccess!(result, input, snapshot, context);
      void queryClient.invalidateQueries({
        queryKey: specificationsListQueryKeyPrefix,
        refetchType: "none",
      });
    },
    onSettled: base.onSettled,
  };
}

/** The summary as the header's keys cache it. */
type CachedSummary = Awaited<ReturnType<typeof specificationSummary>>;

export function useEditSpecificationSummary() {
  const queryClient = useQueryClient();
  return useMutation(editSpecificationSummaryMutationOptions(queryClient));
}
